/**
 * ResultsTab helpers — sentinel-tagged flat string[] model.
 *
 * Sentinel format:
 *   - Result entries:  raw uuid string (e.g. "abc-123-...")
 *   - Divider entries: `divider:<ISO>` (e.g. "divider:2026-01-31T23:59:59.999Z")
 *
 * Collision: uuids are RFC-4122 v4 and never start with "divider:", so the
 * namespaces are disjoint by construction.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { filterItemsForInterval } from '../../utils/statusUtils';

const DIVIDER_PREFIX = 'divider:' as const;

// ---------------------------------------------------------------------------
// Type definitions
// ---------------------------------------------------------------------------

/** Narrow type for a divider sentinel entry in the flat order array. */
export type DividerSentinel = `divider:${string}`;

/** Union of the two entry shapes in the flat order array. */
export type ResultsOrderEntry = string | DividerSentinel;

// ---------------------------------------------------------------------------
// Sentinel helpers
// ---------------------------------------------------------------------------

/** Type guard — true when the entry is a divider sentinel. */
export function isDividerEntry(entry: string): entry is DividerSentinel {
  return entry.startsWith(DIVIDER_PREFIX);
}

/** Extract the ISO date string from a divider sentinel. */
export function dividerIso(entry: DividerSentinel): string {
  return entry.slice(DIVIDER_PREFIX.length);
}

/** Create a divider sentinel from an ISO string. */
export function makeDividerEntry(isoString: string): DividerSentinel {
  return `${DIVIDER_PREFIX}${isoString}` as DividerSentinel;
}

// ---------------------------------------------------------------------------
// Interval / result types (minimal — only what the helpers need)
// ---------------------------------------------------------------------------

interface Interval {
  start: Date;
  end: Date;
}

interface ResultLike {
  uuid: string;
  deadline?: string | null;
  forIntervalEnding?: string;  // optional — enables reuse with filterItemsForInterval
}

interface ObjectiveLike {
  results: ResultLike[];
}

// ---------------------------------------------------------------------------
// buildResultsOrder — pure function, replaces initialOrder useMemo
// ---------------------------------------------------------------------------

/**
 * Derive the flat sentinel-tagged order array from the objective and map config.
 *
 * Returns a `string[]` where:
 *   - Divider entries: `divider:<ISO>` (interval end date)
 *   - Result entries:  raw uuid string
 *
 * Results are placed under the divider for the interval their deadline falls in.
 * Results whose deadline doesn't fall in any known interval are placed under
 * the first divider (or dropped if there are no intervals at all).
 */
export function buildResultsOrder(
  objective: ObjectiveLike,
  allPossibleIntervals: Interval[],
  showAllDates: boolean,
): string[] {
  // Cast results for use with filterItemsForInterval — Result1 always has
  // forIntervalEnding, but ResultLike keeps it optional for interface flexibility.
  // The cast to unknown is needed because deadline?: string | null is wider than
  // the helper's deadline?: string constraint.
  type FilterableResult = { uuid: string; deadline?: string; forIntervalEnding: string };
  const filterableResults = objective.results as unknown as FilterableResult[];

  // Determine which intervals to show
  const intervalsToShow = new Set<Interval>();

  // Always include intervals that have results
  for (const interval of allPossibleIntervals) {
    if (filterItemsForInterval(filterableResults, interval.start, interval.end).length > 0) {
      intervalsToShow.add(interval);
    }
  }

  // If showing all dates, add all possible intervals
  if (showAllDates) {
    allPossibleIntervals.forEach((interval) => {
      intervalsToShow.add(interval);
    });
  }

  // Sort by interval end date
  const sortedIntervals = Array.from(intervalsToShow).sort(
    (a, b) => a.end.getTime() - b.end.getTime(),
  );

  const entries: string[] = [];
  sortedIntervals.forEach((interval) => {
    const iso = interval.end.toISOString();
    entries.push(makeDividerEntry(iso));

    const resultsForInterval = filterItemsForInterval(
      filterableResults,
      interval.start,
      interval.end,
    );

    resultsForInterval.forEach((result) => {
      entries.push(result.uuid);
    });
  });

  return entries;
}

// ---------------------------------------------------------------------------
// deadlineWalker — extracted from handleReorder for testability
// ---------------------------------------------------------------------------

interface DeadlineWalkInput {
  newOrder: string[];
  allPossibleIntervals: Interval[];
  existingResults: ResultLike[];
}

interface DeadlineWalkOutput {
  uuid: string;
  resolvedDeadline: string;
}

/**
 * Walk a reordered sentinel-tagged array and derive the new deadline for
 * each result uuid.
 *
 * Invariant: no result is ever returned with an empty-string deadline.
 * Fallback chain: allPossibleIntervals[0].end.toISOString() || existing deadline.
 */
export function walkDeadlines({
  newOrder,
  allPossibleIntervals,
  existingResults,
}: DeadlineWalkInput): DeadlineWalkOutput[] {
  let currentDeadlineIso = '';
  const output: DeadlineWalkOutput[] = [];
  const resultByUuid = new Map(existingResults.map((r) => [r.uuid, r]));

  for (const entry of newOrder) {
    if (isDividerEntry(entry)) {
      currentDeadlineIso = dividerIso(entry);
    } else {
      // uuid entry
      const base = resultByUuid.get(entry);
      const resolvedDeadline =
        currentDeadlineIso ||
        allPossibleIntervals[0]?.end.toISOString() ||
        base?.deadline ||
        '';
      output.push({ uuid: entry, resolvedDeadline });
    }
  }

  return output;
}

// ---------------------------------------------------------------------------
// useResultsOrderReconciliation — hook for set-membership reconciliation
// ---------------------------------------------------------------------------

interface ReconciliationOptions {
  objective: ObjectiveLike;
  allPossibleIntervals: Interval[];
  showAllDates: boolean;
}

interface ReconciliationResult {
  resultsOrder: string[];
  setResultsOrder: (order: string[]) => void;
}

/**
 * Manages `resultsOrder: string[]` state with set-membership reconciliation.
 *
 * Rules:
 *  - Initialises from `buildResultsOrder(...)`.
 *  - When the UUID set changes (add/remove) OR `showAllDates` toggles:
 *    rebuild by (a) keeping existing local order for surviving uuids,
 *    (b) inserting new uuids at their positions from `expectedOrder`,
 *    (c) dropping removed uuids.
 *  - Does NOT reconcile on order-only differences (e.g. server echo of our
 *    own successful drag).
 *  - Does NOT use an `isDragging` guard — set-membership reconciliation is
 *    strictly better because concurrent-collaborator add/remove still
 *    propagates in real time.
 */
export function useResultsOrderReconciliation({
  objective,
  allPossibleIntervals,
  showAllDates,
}: ReconciliationOptions): ReconciliationResult {
  const expectedOrder = useMemo(
    () => buildResultsOrder(objective, allPossibleIntervals, showAllDates),
    [objective, allPossibleIntervals, showAllDates],
  );

  const [resultsOrder, setResultsOrder] = useState<string[]>(expectedOrder);

  const prevUuidSetRef = useRef<string | null>(null);
  const prevShowAllDatesRef = useRef<boolean>(showAllDates);

  useEffect(() => {
    const currentUuidSet = objective.results
      .map((r) => r.uuid)
      .slice()
      .sort()
      .join(',');

    // First-mount init — useState already holds the expected order
    if (prevUuidSetRef.current === null) {
      prevUuidSetRef.current = currentUuidSet;
      prevShowAllDatesRef.current = showAllDates;
      return;
    }

    const showAllDatesChanged = showAllDates !== prevShowAllDatesRef.current;

    if (currentUuidSet !== prevUuidSetRef.current || showAllDatesChanged) {
      prevUuidSetRef.current = currentUuidSet;
      prevShowAllDatesRef.current = showAllDates;

      // Rebuild preserving local order for surviving uuids.
      // expectedOrder is the source of truth for dividers and new uuids.
      setResultsOrder((prevOrder) => {
        const survivingUuids = new Set(objective.results.map((r) => r.uuid));
        const prevUuids = prevOrder.filter((e) => !isDividerEntry(e));
        const prevUuidSet = new Set(prevUuids);

        // Uuids that survive in their existing local order
        const survivingInLocalOrder = prevOrder.filter(
          (e) => isDividerEntry(e) || survivingUuids.has(e),
        );

        // New uuids not previously seen — gather from expectedOrder in order
        const newUuidSet = new Set<string>();
        for (const e of expectedOrder) {
          if (!isDividerEntry(e) && !prevUuidSet.has(e)) newUuidSet.add(e);
        }

        if (newUuidSet.size === 0 && !showAllDatesChanged) {
          // Only drops and/or divider-set changes — rebuild from expected
          // while preserving relative order of surviving uuids
          const result: string[] = [];
          let uuidCursor = survivingInLocalOrder.filter((e) => !isDividerEntry(e));
          let uuidIndex = 0;

          for (const entry of expectedOrder) {
            if (isDividerEntry(entry)) {
              result.push(entry);
            } else {
              if (uuidIndex < uuidCursor.length) {
                result.push(uuidCursor[uuidIndex++]);
              }
            }
          }
          return result;
        }

        // Has new uuids or showAllDates toggled — use expectedOrder as base
        // but slot in local ordering for surviving uuids
        const survivingLocalUuidOrder = survivingInLocalOrder.filter((e) => !isDividerEntry(e));
        let localIdx = 0;
        const result: string[] = [];

        for (const entry of expectedOrder) {
          if (isDividerEntry(entry)) {
            result.push(entry);
          } else if (survivingUuids.has(entry) && !newUuidSet.has(entry)) {
            // Use local ordering for survivors
            if (localIdx < survivingLocalUuidOrder.length) {
              result.push(survivingLocalUuidOrder[localIdx++]);
            } else {
              result.push(entry);
            }
          } else {
            // New uuid — insert at expected position
            result.push(entry);
          }
        }
        return result;
      });
    }
  }, [expectedOrder, objective.results, showAllDates]);

  return { resultsOrder, setResultsOrder };
}
